import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import CraneSpecificationManager from './components/CraneSpecificationManager';
import UserManagement from './components/UserManagement';
import CompCuration from './components/CompCuration';
import SystemMonitoring from './components/SystemMonitoring';
import AuditLogging from './components/AuditLogging';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('specifications');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/crane-search-dashboard' },
    { label: 'Admin Panel' }
  ];

  const tabs = [
    {
      id: 'specifications',
      label: 'Crane Specifications',
      icon: 'Database',
      description: 'Manage crane database and specifications'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: 'Users',
      description: 'Control user roles and subscriptions'
    },
    {
      id: 'comps',
      label: 'Comp Curation',
      icon: 'CheckCircle',
      description: 'Review and approve comparable sales'
    },
    {
      id: 'monitoring',
      label: 'System Monitoring',
      icon: 'Activity',
      description: 'Monitor system health and performance'
    },
    {
      id: 'audit',
      label: 'Audit Logging',
      icon: 'FileText',
      description: 'View system audit logs and changes'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'specifications':
        return <CraneSpecificationManager />;
      case 'users':
        return <UserManagement />;
      case 'comps':
        return <CompCuration />;
      case 'monitoring':
        return <SystemMonitoring />;
      case 'audit':
        return <AuditLogging />;
      default:
        return <CraneSpecificationManager />;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="px-6 py-6">
        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} className="mb-6" />

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-text-primary mb-2">Admin Panel</h1>
              <p className="text-text-secondary">
                Comprehensive system administration for CranePrice platform
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => console.log('System backup initiated')}
                iconName="Database"
                iconPosition="left"
                size="sm"
              >
                System Backup
              </Button>
              <Button
                variant="outline"
                onClick={() => console.log('Generate system report')}
                iconName="FileText"
                iconPosition="left"
                size="sm"
              >
                Generate Report
              </Button>
            </div>
          </div>
        </div>

        {/* Admin Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Users" size={24} className="text-primary" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Total Users</p>
                <p className="text-2xl font-bold text-text-primary">2,847</p>
                <p className="text-xs text-success">+12% this month</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Database" size={24} className="text-success" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Crane Models</p>
                <p className="text-2xl font-bold text-text-primary">1,247</p>
                <p className="text-xs text-success">+8 this week</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={24} className="text-warning" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">Pending Reviews</p>
                <p className="text-2xl font-bold text-text-primary">127</p>
                <p className="text-xs text-warning">Needs attention</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Activity" size={24} className="text-accent" />
              </div>
              <div>
                <p className="text-sm text-text-secondary">System Health</p>
                <p className="text-2xl font-bold text-text-primary">98.5%</p>
                <p className="text-xs text-success">Excellent</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-card border border-border rounded-lg mb-6">
          <div className="border-b border-border">
            <nav className="flex overflow-x-auto">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-3 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary bg-primary/5' :'border-transparent text-text-secondary hover:text-text-primary hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={18} />
                  <div className="text-left">
                    <p className="font-medium">{tab?.label}</p>
                    <p className="text-xs opacity-75 hidden sm:block">{tab?.description}</p>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-card border border-border rounded-lg p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;